import React, { useState, useEffect, useRef, useContext } from 'react';
import Helmet from 'react-helmet';
import { setOptions, importLibrary } from '@googlemaps/js-api-loader';

import { Tooltip } from '+/components/Tooltip';
import { t } from '+/utils/textDictionary';
import selectors from '+/components/DonateForm/selectors';
import analyticsEvent from '+/utils/analyticsEvent';
import { FormFieldErrorsContext } from '+/contexts/FormFieldErrorsContext';
import { RememberMeData, useRememberMe } from '+/contexts';

let autoComplete: google.maps.places.Autocomplete | undefined;
// Fix: Add a flag to ensure setOptions is only called once per session
let isApiConfigured = false;

/**
 * Reveal the address fieldset and make fields visible to screen readers.
 */
function reveal() {
  const addressFieldset: HTMLElement | null = document.querySelector(selectors.ADDRESS_DETAILS);

  if (addressFieldset) {
    addressFieldset.className += ' reveal';
  }

  const elementsToReveal = document.querySelectorAll('fieldset#AddressFieldset *');

  elementsToReveal.forEach(element => {
    element.removeAttribute('aria-hidden');
    element.removeAttribute('tabindex');
  });
}

/**
 * Reveal and then focus Address Line 2.
 */
function revealAndFocus(controller) {
  reveal();
  const address2input: HTMLElement | null = document.querySelector(selectors.ADDRESS_LINE2_INPUT);
  setTimeout(() => {
    address2input?.focus();
  }, 100);
}

/**
 * Handle place selection from autocomplete or remember-me data.
 */
async function handlePlaceSelect(
  updateAddress,
  controller,
  rememberMeData: Pick<
    RememberMeData,
    'address1' | 'postcode' | 'locality' | 'state' | 'country'
  > | null = null,
) {
  let address1 = '';
  let postcode = '';
  let locality = '';
  let state = '';
  let country = '';

  if (rememberMeData) {
    address1 = rememberMeData.address1;
    postcode = rememberMeData.postcode;
    locality = rememberMeData.locality;
    state = rememberMeData.state;
    country = rememberMeData.country;

    controller.setMailingAddress(address1, locality, state, postcode, country);
  } else {
    if (!autoComplete) return;

    const place = autoComplete.getPlace();
    if (!place.address_components) return;

    const countryComponent = place.address_components.find(c => c.types[0] === 'country');
    country = countryComponent?.short_name || '';

    for (const component of place.address_components) {
      const componentType = component.types[0];

      switch (componentType) {
        case 'street_number': {
          address1 = `${component.long_name} ${address1}`;
          break;
        }
        case 'route': {
          address1 += component.short_name;
          break;
        }
        case 'postal_code': {
          postcode = `${component.long_name}${postcode}`;
          break;
        }
        case 'postal_code_suffix': {
          postcode = `${postcode}-${component.long_name}`;
          break;
        }
        case 'postal_code_prefix': {
          if (!postcode) {
            postcode = component.long_name;
            break;
          }
          postcode = `${component.long_name}-${postcode}`;
          break;
        }
        case 'locality':
        case 'sublocality_level_1':
        case 'postal_town': {
          locality = locality || component.long_name;
          break;
        }
        case 'administrative_area_level_2': {
          if (country !== 'GB') break;
          state = component.short_name;
          break;
        }
        case 'administrative_area_level_1': {
          state = state || component.short_name;
          break;
        }
        default:
          break;
      }
    }

    controller.setMailingAddress(address1, locality, state, postcode, country);
  }

  analyticsEvent({
    eventTypeName: 'CustomFormEvent',
    targetElement: 'EveryActionForm',
    targetElementVariation: 'Donation',
    triggerAction: 'Address Autocompleted',
    simpleName: 'Address filled with autocomplete',
  });

  updateAddress(address1);
  revealAndFocus(controller);
  controller.renderAddressFeedback();
}

/**
 * Autofill from remember-me data.
 * (No handlePlaceSelect param here to avoid shadowing.)
 */
const handleRememberMeAutofill = (
  revealFn,
  updateAddress,
  controller,
  rememberMeData: RememberMeData,
) => {
  const { address1, postcode, locality, state, country } = rememberMeData;

  handlePlaceSelect(updateAddress, controller, { address1, postcode, locality, state, country });

  if (rememberMeData?.address2) {
    const address2s = document.getElementsByName('AddressLine2');

    if (address2s[0]) {
      (address2s[0] as HTMLInputElement).value = rememberMeData?.address2;
    }
  }

  revealFn();
};

const handleScriptLoad = (updateAddress, autoCompleteRef, controller) => {
  if (!window.google || !window.google.maps || !window.google.maps.places) return;

  autoComplete = new window.google.maps.places.Autocomplete(autoCompleteRef.current, {
    fields: ['address_components'],
    types: ['address'],
  });

  autoComplete.addListener('place_changed', () => {
    handlePlaceSelect(updateAddress, controller, null);
  });
};

export const MailingAddressAutocomplete = ({ controller }) => {
  const [address, setAddress] = useState(controller.getAddressLine1() || '');
  const [isAutocompleteExpanded, setIsAutoCompleteExpanded] = useState(false);
  const autoCompleteRef = useRef<HTMLInputElement | null>(null);

  const { errors, addErrors } = useContext(FormFieldErrorsContext);
  const addressError = 'Address is required.';
  const showError = errors.find(err => err.text === addressError);

  const { state } = useRememberMe();
  const { userData: rememberMeData } = state;

  /**
   * Autofill from "remember me" data when it becomes available.
   */
  useEffect(() => {
    if (!rememberMeData) return;
    handleRememberMeAutofill(reveal, setAddress, controller, rememberMeData);
  }, [rememberMeData]); // controller is stable from props; OK to omit

  const updateIsAutocompleteExpanded = () => {
    const autoCompleteMenu = document.getElementsByClassName('pac-container');
    setTimeout(() => {
      if (
        !!autoCompleteMenu &&
        autoCompleteMenu.length > 0 &&
        window.getComputedStyle(autoCompleteMenu[0] as Element).getPropertyValue('display') !==
          'none'
      ) {
        // Ensure the listbox has an ID that matches aria-controls
        (autoCompleteMenu[0] as HTMLElement).setAttribute(
          'id',
          'google-places-autocomplete-listbox',
        );
        setIsAutoCompleteExpanded(true);
      } else {
        setIsAutoCompleteExpanded(false);
      }
    }, 500);
  };

  const handleOnChange = (val: string) => {
    controller.setAddressLine1(val);
    setAddress(val);
    if (showError) {
      controller.renderFeedback();
      addErrors(controller.getErrors());
      autoCompleteRef.current?.focus();
    }
    updateIsAutocompleteExpanded();
  };

  const handleOnBlur = () => {
    autoCompleteRef.current?.setAttribute('autocomplete', 'off');
    if (address) {
      reveal();
    }
    updateIsAutocompleteExpanded();
  };

  const handleOnFocus = () => {
    autoCompleteRef.current?.setAttribute('autocomplete', 'new-password');
    updateIsAutocompleteExpanded();
  };

  const handleOnKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
    updateIsAutocompleteExpanded();
  };

  /**
   * Reveal the address fieldset if the controller already has address fields.
   * This only needs to run once on mount.
   */
  useEffect(() => {
    if (controller.hasAddressFieldEntered()) {
      reveal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally only on mount

  /**
   * Load Google Maps JS API (Places library) once in the browser and init Autocomplete.
   */
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Fix: Only call setOptions if it hasn't been called yet
    if (!isApiConfigured) {
      setOptions({
        key: process.env.GATSBY_GOOGLE_MAPS_API_KEY as string,
        v: 'weekly',
        libraries: ['places'],
      });
      isApiConfigured = true;
    }

    (async () => {
      try {
        await importLibrary('places');
        handleScriptLoad(setAddress, autoCompleteRef, controller);
      } catch (e) {
        console.error('Failed to load Google Maps Places library', e);
      }
    })();

    const elementsToHide = document.querySelectorAll('fieldset#AddressFieldset *');

    elementsToHide.forEach(element => {
      element.setAttribute('aria-hidden', 'true');
      element.setAttribute('tabindex', '-1');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // load script & hide elements only once on mount

  /**
   * Handle browser autofill that may populate address line 1 slightly
   * after initial render. We intentionally check this every render until
   * we have a local address value.
   */
  useEffect(() => {
    if (!address) {
      const controllerAddress = controller.getAddressLine1();
      if (controllerAddress) {
        setAddress(controllerAddress);
        reveal();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });

  /**
   * Reset errors when the component mounts or when controller changes.
   */
  useEffect(() => {
    addErrors(controller.getErrors());
  }, [controller, addErrors]);

  return (
    <>
      <Helmet>
        <link rel="preconnect" href="https://maps.googleapis.com" />
      </Helmet>
      <label htmlFor="AutoMailingAddress" className="search-location-input">
        <input
          type="text"
          ref={autoCompleteRef}
          onChange={e => handleOnChange(e.target.value)}
          onBlur={handleOnBlur}
          onFocus={handleOnFocus}
          onKeyDown={handleOnKeyDown}
          title={t('Widget.Address.AddressLine1')}
          name="AutoMailingAddress"
          id="AutoMailingAddress"
          required
          value={address}
          autoComplete="off"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={isAutocompleteExpanded}
          aria-controls="google-places-autocomplete-listbox"
          aria-describedby="autocomplete-accessible-description"
        />
        <span className="totally-hidden" id="autocomplete-accessible-description">
          {t('Widget.Address.A11yDescription')}
        </span>
        <span className="visually-hidden" role="status" aria-live="polite">
          {isAutocompleteExpanded && t('Widget.Address.A11yResultsAvailable')}
        </span>
        {showError && <small className="error">{addressError}</small>}
        <Tooltip
          teaser={t('Widget.Tooltip.AddressTeaser')}
          content={t('Widget.Tooltip.AddressContent')}
        />
      </label>
    </>
  );
};
